import { FunctionComponent, useState } from 'react';
import cn from 'classnames';
import { useForm, FormProvider } from 'react-hook-form';
import { useParams } from 'react-router';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Section, SectionHeading } from '../../components';
import { FormSpec, StateSetupProps } from '../../types';
import { cleanIntakeDates } from '../../utils/intake';
import { successCallbackPath } from '../../utils/paths';
import { getRegionNameByCode } from '../../utils/format';
import { fetchApi } from '../../utils/fetchApi';
import { ApiStatus, IApiData } from '../../utils/types';
import { Renderer, formSpecToRendererConfig } from '../../components/forms';
import CoSpec from '../../components/forms/specs/state_setup/co_spec.json';
import FlSpec from '../../components/forms/specs/state_setup/fl_spec.json';
import IlSpec from '../../components/forms/specs/state_setup/il_spec.json';
import MaSpec from '../../components/forms/specs/state_setup/ma_spec.json';
import MoSpec from '../../components/forms/specs/state_setup/mo_spec.json';
import NcSpec from '../../components/forms/specs/state_setup/nc_spec.json';
import NySpec from '../../components/forms/specs/state_setup/ny_spec.json';
import OfficersSpec from '../../components/forms/specs/state_setup/profile_officers_spec.json';
import OhSpec from '../../components/forms/specs/state_setup/oh_spec.json';
import OkSpec from '../../components/forms/specs/state_setup/ok_spec.json';
import ScSpec from '../../components/forms/specs/state_setup/sc_spec.json';
import ScUiOwnersSpec from '../../components/forms/specs/state_setup/sc_ui_owners_spec.json';
import ScSalseAndUseSpec from '../../components/forms/specs/state_setup/sc_sales_and_use_spec.json';
import TnSpec from '../../components/forms/specs/state_setup/tn_spec.json';
import TxSpec from '../../components/forms/specs/state_setup/tx_spec.json';
import UtSpec from '../../components/forms/specs/state_setup/ut_spec.json';
import WaSpec from '../../components/forms/specs/state_setup/wa_spec.json';
import DefaultSpec from '../../components/forms/specs/default_spec.json';
import { useScrollIntoView } from '../../hooks';
import { FormError } from '../../components/forms/FormError';

export const ManagedReqsByState: Record<string, string[]> = {
  al: ['5e40808e', '62b9d11b'],
  ak: ['e48717c5'],
  az: ['ee40f009', 'c7b2c51f'],
  ar: ['345c60f8', '8198aa2e'],
  ca: ['ffcf700d'],
  ct: ['e81a9bc8', 'd8af67d2', '1f56bf2d'],
  de: ['c06b95f7', '743505dd'],
  co: ['5f2efd18', '3f15ccab', 'd7b55a6f'],
  dc: ['b0c604ee', '771c137f', 'b38ca80f'],
  fl: ['a6ca5d56'],
  ga: ['9f1897c8', 'f950b60a'],
  hi: ['750ecd24', 'd2fc06b1'],
  id: ['20360481', '1569481a'],
  il: ['0f30b82a', '2c1881dd'],
  in: ['aed70104', 'd2bc6a52'],
  ia: ['8463275e', 'f7bdcd43'],
  ks: ['9275a8db', 'c6ad5e0f'],
  ky: ['5735dc57', '0e32857c'],
  la: ['8d703088', '7ea8a0cc'],
  ma: ['dba9b4ba', '71350337', '06995bd7'],
  md: ['7d1edcf5', '81b07a98'],
  me: ['93fedb78', '83af7e81'],
  mi: ['282e730b', '3958e5a0'],
  mn: ['c5c03314', 'debbc058'],
  mo: ['b0c27790', '49d1ce6c'],
  ms: ['28f9d710', '80c25004'],
  ne: ['c8e60eae', '80a1d5c9'],
  nc: ['bf9b4638', 'b769af08'],
  nd: ['048a4766', '6090d9f9', 'e2b86d8e'],
  nj: ['8edbc60d', '949672da'],
  nm: ['1827f2e0', '6970bebd'],
  nv: ['269fe702', '7b57eb19'],
  nh: ['388ea3fb'],
  ny: ['53adab6a', '8c6421f1'],
  oh: ['013945c9', '3a05ba55', 'd6e49eb0'],
  ok: ['4e7ba9e7', '004cc99f'],
  or: ['0db9ec74', '2d006e72'],
  pa: ['1e98e265', '99bff5f2'],
  mt: ['e752ebeb', '0f81eea5'],
  ri: ['af89d508', '07be759f'],
  sc: ['bba7a741', '386a5a80'],
  sd: ['bf1894ee'],
  tn: ['462455f2'],
  tx: ['bc070b35'],
  ut: ['9ac8def5', '6895a958'],
  vt: ['078662ed', '50e7933b'],
  va: ['1621e77b', 'c35bb4de'],
  wa: ['a47171e5', '6a8f39c4', '91733fbc'],
  wv: ['ab736e83', 'e2c76e97'],
  wi: ['28d8583e', '534d6fd6'],
  wy: ['9580f877', 'e5f7ec3c', '77de70ba'],
};

export const StateSpecMapping: Record<string, FormSpec> = {
  co: CoSpec,
  fl: FlSpec,
  il: IlSpec,
  ma: MaSpec,
  mo: MoSpec,
  nc: NcSpec,
  ny: NySpec,
  oh: OhSpec,
  ok: OkSpec,
  sc: ScSpec,
  tn: TnSpec,
  tx: TxSpec,
  ut: UtSpec,
  wa: WaSpec,
  scuiowners: ScUiOwnersSpec,
  scsalesanduse: ScSalseAndUseSpec,
  'supplemental-officers': OfficersSpec,
};

interface GenericIntakeProps {
  scrollOnMount?: boolean;
  align?: 'start' | 'center' | 'end';
  isFullscreen?: boolean;
  isFullWidth?: boolean;
}

export const GenericIntake: FunctionComponent<
  StateSetupProps & GenericIntakeProps
> = ({
  peopleData,
  onSubmitSuccess,
  onEnterLater,
  scrollOnMount = true,
  align = 'center',
  isFullscreen,
  isFullWidth,
}) => {
  const [scrollIntoViewRef] = useScrollIntoView(scrollOnMount);
  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get('callback_url');
  const { locationId: paramLocationId } = useParams<Record<string, string>>();
  const [hasSubmitError, setHasSubmitError] = useState<boolean>(false);
  const locationId = paramLocationId?.toLowerCase() || 'does-not-exist';
  const isSpecSupported =
    Object.keys(StateSpecMapping).indexOf(locationId) > -1;

  const navigate = useNavigate();

  const formMethods = useForm();
  const {
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const handleSubmitSuccess = () =>
    onSubmitSuccess
      ? onSubmitSuccess()
      : navigate(`/state-setup/${locationId}/success`);

  const onSubmit = (data: any) => {
    setHasSubmitError(false);

    const cleanedData = cleanIntakeDates(data);
    const payload = isSpecSupported
      ? { data: cleanedData }
      : { data: { default: cleanedData } };

    fetchApi({
      url: `/api/form_captures/${locationId}`,
      method: 'POST',
      body: payload,
    }).then(({ status }: IApiData) => {
      if (
        status === ApiStatus.Error ||
        status === ApiStatus.ErrorUnauthorized
      ) {
        setHasSubmitError(true);
      } else {
        let reqUpdateError = false;
        if (ManagedReqsByState[locationId]) {
          const reqIds: string[] = ManagedReqsByState[locationId];
          // Set the requirement to managed and in-progress
          fetchApi({
            url: `/api/requirements/batch`,
            method: 'PATCH',
            body: {
              patch: { operation: 'mark-as-managed' },
              data_ids: reqIds,
            },
          }).then(({ status: status2 }: IApiData) => {
            if (status2 === ApiStatus.Error) {
              reqUpdateError = true;
              setHasSubmitError(true);
            }
          });
          // Create and start workflow for requirements
          fetchApi({
            url: `/api/workflows`,
            method: 'POST',
            body: {
              data_ids: reqIds,
              region_code: locationId,
            },
          }).then(({ status: status2 }: IApiData) => {
            if (status2 === ApiStatus.Error) {
              reqUpdateError = true;
              setHasSubmitError(true);
            }

            if (callbackUrl) {
              navigate(successCallbackPath(callbackUrl), {
                replace: true,
              });
              return;
            }

            if (!reqUpdateError) {
              handleSubmitSuccess();
            }
          });
        }
      }
    });
  };

  const loadedSpec = isSpecSupported
    ? StateSpecMapping[locationId]
    : DefaultSpec;

  return (
    <Section
      className={cn(`flex flex-col`, {
        'items-start': align === 'start',
        'items-center': align === 'center',
        'items-end': align === 'end',
      })}
      ref={scrollIntoViewRef}
      isFullscreen={isFullscreen}
    >
      <SectionHeading
        className={cn('pb-4', {
          'w-full': isFullWidth,
          'w-96': !isFullWidth,
        })}
        text={getRegionNameByCode(locationId) + ' Employer Setup'}
      />
      <FormProvider {...formMethods}>
        <form
          className={cn('my-8 ', {
            'w-full': isFullWidth,
            'w-96': !isFullWidth,
          })}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Renderer
            config={formSpecToRendererConfig(loadedSpec)}
            errors={errors}
            peopleData={peopleData}
          />
          <div className="mt-4 flex">
            <div className="flex gap-x-4">
              {onEnterLater && (
                <Button
                  type="button"
                  variant="grayTertiary"
                  size="large"
                  isFullWidth
                  onClick={onEnterLater}
                >
                  Enter later
                </Button>
              )}
              <Button type="submit" isFullWidth size="large">
                Submit
              </Button>
            </div>
            {hasSubmitError && <FormError />}
          </div>
        </form>
      </FormProvider>
    </Section>
  );
};

export default GenericIntake;
